import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

import { AppShell } from '@/components/layout/app-shell';
import { LoadingPage } from '@/components/layout/loading-page';
import { ProtectedRoute } from '@/components/layout/protected-route';
import { RoleGate } from '@/components/layout/role-gate';
import { Toaster } from '@/components/ui/toaster';
import { AuthCallbackPage } from '@/features/auth/auth-callback-page';
import { ConnectErrorPage } from '@/features/auth/connect-error-page';
import { ConnectPage } from '@/features/auth/connect-page';
import { ConnectSuccessPage } from '@/features/auth/connect-success-page';
import { LoginPage } from '@/features/auth/login-page';
import { initializeApiClient } from '@/lib/api-client';

const ExecutiveSummaryPage = lazy(() =>
  import('@/features/dashboard/executive-summary-page').then((m) => ({ default: m.ExecutiveSummaryPage })),
);
const EngagementPage = lazy(() =>
  import('@/features/engagement/engagement-page').then((m) => ({ default: m.EngagementPage })),
);
const CompliancePage = lazy(() =>
  import('@/features/compliance/compliance-page').then((m) => ({ default: m.CompliancePage })),
);
const OutcomesPage = lazy(() =>
  import('@/features/outcomes/outcomes-page').then((m) => ({ default: m.OutcomesPage })),
);
const RoiPage = lazy(() =>
  import('@/features/roi/roi-page').then((m) => ({ default: m.RoiPage })),
);
const ReportsPage = lazy(() =>
  import('@/features/reports/reports-page').then((m) => ({ default: m.ReportsPage })),
);
const LaunchKitPage = lazy(() =>
  import('@/features/launch-kit/launch-kit-page').then((m) => ({ default: m.LaunchKitPage })),
);
const SettingsPage = lazy(() =>
  import('@/features/settings/settings-page').then((m) => ({ default: m.SettingsPage })),
);

initializeApiClient();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename="/">
        <Suspense fallback={<LoadingPage />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            {/* Public self-service Azure AD admin-consent flow (no auth). */}
            <Route path="/connect" element={<ConnectPage />} />
            <Route path="/connect/success" element={<ConnectSuccessPage />} />
            <Route path="/connect/error" element={<ConnectErrorPage />} />
            <Route
              element={
                <ProtectedRoute>
                  <RoleGate>
                    <AppShell />
                  </RoleGate>
                </ProtectedRoute>
              }
            >
              <Route index element={<ExecutiveSummaryPage />} />
              <Route path="engagement" element={<EngagementPage />} />
              <Route path="compliance" element={<CompliancePage />} />
              <Route path="outcomes" element={<OutcomesPage />} />
              <Route path="roi" element={<RoiPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="launch-kit" element={<LaunchKitPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster />
    </QueryClientProvider>
  );
}
